import {
  colorBackgroundAlt,
  colorBaseNeutralWhite,
  colorBrandPrimaryBold,
  colorBrandPrimarySoftest,
  colorContentDim,
  colorStatusAlertBold,
  colorStatusAlertSoftest,
  colorStatusNeutralBold,
  colorStatusNeutralSoftest,
  colorStatusPositiveBold,
  colorStatusPositiveSoftest,
} from '@duxton-web/design-tokens/themes/light';
import { AiOutlined, DownloadUniversal, EditOutlined } from '@duxton-web/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CONTENT_GENERATION_LIST_ATTRIBUTES,
  CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS,
  CONTENT_GENERATION_GENERATE_MYSTIQUE_V1,
  contentGenerationGenerateMystiqueV1,
  contentGenerationGetCountriesConfig,
  contentGenerationListAttributes,
  contentGenerationListProductOfferings,
  cpGetCountries,
  cpGetVerticals,
  cpListAttributes,
  cpListCvps,
  cpListProducts,
} from 'omega-rtc/actions/ucd';
import { VERTICALS_DISPLAY_NAMES } from 'omega-rtc/constants/ucd';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import {
  STATUS_ACTIVE,
  attributeFromApi,
  countryFromApi,
  langLabel,
  productDisplayName,
  productFromApi,
  verticalFromApi,
} from './content-config/adapters';
import {
  buildContentPortalProductOfferingConfig,
  buildMystiqueV1OverridePayload,
  getContentPortalAttributeOptions,
  getContentPortalProductOptions,
  isContentPortalProductsEnabled,
  parseMystiqueContentItems,
} from './ContentGenerationView.helpers';

const MAX_ATTRIBUTES = 3;
const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT: Record<string, any> = {};

const CUSTOM_PO_KEY = '__custom__';
const CUSTOM_PO_OPTION = { value: CUSTOM_PO_KEY, label: 'Custom' };


interface CountryConfig {
  code: string;
  name: string;
  verticals: { code: string; name: string }[];
  languages: { code: string; name: string }[];
}

interface AttributeOption {
  value: string;
  label: string;
  isWhitelisted: boolean;
  definition?: string;
}

interface RawAttribute {
  key: string;
  isWhitelisted: boolean;
}

interface ProductOfferingConfig {
  key: string;
  displayName: string;
  hasDisplayNameOverride?: boolean;
  productOverview: Record<string, string>;
  reasonToUse: Record<string, string>;
  callToAction: Record<string, string>;
  cvp: { exclusions: string[]; dependencies: string[] };
  personalAttributes: any;
}

interface FormValues {
  country: string;
  vertical: string;
  language: string;
  productOffering: string;
  attributes: string[];
  dateRange?: [Dayjs, Dayjs];
}


const ContentGenerationView: React.FC = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [contentItems, setContentItems] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState(null);
  const emptyAttributeOptions: AttributeOption[] = [];
  const [attributeOptions, setAttributeOptions] = useState(emptyAttributeOptions);
  const emptyProductOfferingOptions: { value: string; label: string }[] = [];
  const [productOfferingOptions, setProductOfferingOptions] = useState(emptyProductOfferingOptions);
  const [excludedCvps, setExcludedCvps] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const noEditingConfig: ProductOfferingConfig | null = null;
  const [editingConfig, setEditingConfig] = useState(noEditingConfig);
  const [editingLanguage, setEditingLanguage] = useState('en');
  const emptyConfigMap: Record<string, ProductOfferingConfig> = {};
  const [productOfferingConfigMap, setProductOfferingConfigMap] = useState(emptyConfigMap);
  const dispatch = useDispatch();
  const isContentPortalProductsEnabledFlag = isContentPortalProductsEnabled();
  const emptyCountriesConfig: CountryConfig[] = [];
  const [countriesConfig, setCountriesConfig] = useState(emptyCountriesConfig);
  const selectedCountry = Form.useWatch('country', form);
  const selectedVertical = Form.useWatch('vertical', form);
  const selectedLanguage = Form.useWatch('language', form);
  const selectedProductOffering = Form.useWatch('productOffering', form);
  const cpCountriesRaw = useSelector((state: any) => _.get(state, 'ucd.cpCountries', EMPTY_ARRAY));
  const cpVerticalsRaw = useSelector((state: any) => _.get(state, 'ucd.cpVerticals', EMPTY_ARRAY));
  const cpAttributesRaw = useSelector((state: any) => _.get(state, 'ucd.cpAttributes', EMPTY_ARRAY));
  const cpProductsRaw = useSelector((state: any) =>
    _.get(state, `ucd.cpProductsByCountry.${selectedCountry}.active`, EMPTY_ARRAY));
  const hasContentPortalCvpsLoaded = useSelector((state: any) =>
    Boolean(selectedCountry && _.has(state, ['ucd', 'cpCvpsByCountry', selectedCountry, 'active'])));
  const cpCvpsRaw = useSelector((state: any) =>
    _.get(state, ['ucd', 'cpCvpsByCountry', selectedCountry, 'active'], EMPTY_ARRAY));
  const cpCountriesRequest = useSelector((state: any) => _.get(state, 'requests.cpGetCountriesRequest', EMPTY_OBJECT));
  const cpVerticalsRequest = useSelector((state: any) => _.get(state, 'requests.cpGetVerticalsRequest', EMPTY_OBJECT));
  const cpAttributesRequest = useSelector((state: any) => _.get(state, 'requests.cpListAttributesRequest', EMPTY_OBJECT));
  const cpProductsRequest = useSelector((state: any) => _.get(state, 'requests.cpListProductsRequest', EMPTY_OBJECT));
  const legacyCountriesConfig = useSelector((state: any) =>
    _.get(state, 'ucd.contentGenerationCountriesConfig', EMPTY_ARRAY));
  const legacyProductOfferings = useSelector((state: any) =>
    _.get(state, 'ucd.contentGenerationProductOfferings', EMPTY_ARRAY));
  const legacyAttributes = useSelector((state: any) =>
    _.get(state, 'ucd.contentGenerationAttributes', EMPTY_ARRAY));
  const legacyContent = useSelector((state: any) =>
    _.get(state, 'ucd.contentGenerationContent', EMPTY_OBJECT));
  const legacyProductOfferingsRequest = useSelector((state: any) =>
    _.get(state, `requests.${getRequestStateKey(CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS)}`, EMPTY_OBJECT));
  const legacyAttributesRequest = useSelector((state: any) =>
    _.get(state, `requests.${getRequestStateKey(CONTENT_GENERATION_LIST_ATTRIBUTES)}`, EMPTY_OBJECT));
  const legacyGenerateRequest = useSelector((state: any) =>
    _.get(state, `requests.${getRequestStateKey(CONTENT_GENERATION_GENERATE_MYSTIQUE_V1)}`, EMPTY_OBJECT));

  useEffect(() => {
    if (isContentPortalProductsEnabledFlag) {
      dispatch(cpGetCountries());
      dispatch(cpGetVerticals());
      dispatch(cpListAttributes());
      return;
    }
    dispatch(contentGenerationGetCountriesConfig());
  }, [dispatch, isContentPortalProductsEnabledFlag]);

  useEffect(() => {
    if (!isContentPortalProductsEnabledFlag) {
      setCountriesConfig(legacyCountriesConfig);
    }
  }, [isContentPortalProductsEnabledFlag, legacyCountriesConfig]);

  useEffect(() => {
    if (isContentPortalProductsEnabledFlag) return;

    if (legacyProductOfferingsRequest.isSuccessful) {
      const configMap: Record<string, ProductOfferingConfig> = {};
      legacyProductOfferings.forEach((o) => {
        configMap[o.key] = o;
      });
      setProductOfferingConfigMap(configMap);
      setProductOfferingOptions([
        ...legacyProductOfferings.map((o) => ({ value: o.key, label: o.displayName })),
        CUSTOM_PO_OPTION,
      ]);
    }

    if (legacyProductOfferingsRequest.isFailure) {
      setProductOfferingOptions([CUSTOM_PO_OPTION]);
    }
  }, [
    isContentPortalProductsEnabledFlag,
    legacyProductOfferings,
    legacyProductOfferingsRequest.isFailure,
    legacyProductOfferingsRequest.isSuccessful,
  ]);

  useEffect(() => {
    if (isContentPortalProductsEnabledFlag) return;

    if (legacyAttributesRequest.isSuccessful) {
      const attributes: RawAttribute[] = legacyAttributes || [];
      setAttributeOptions(attributes.map((a) => ({
        value: a.key,
        label: a.key,
        isWhitelisted: a.isWhitelisted,
      })));
    }

    if (legacyAttributesRequest.isFailure) {
      setAttributeOptions([]);
    }
  }, [
    isContentPortalProductsEnabledFlag,
    legacyAttributes,
    legacyAttributesRequest.isFailure,
    legacyAttributesRequest.isSuccessful,
  ]);

  useEffect(() => {
    if (!isContentPortalProductsEnabledFlag || !selectedCountry) return;
    dispatch(cpListProducts(selectedCountry, STATUS_ACTIVE));
    dispatch(cpListCvps(selectedCountry, STATUS_ACTIVE));
  }, [dispatch, isContentPortalProductsEnabledFlag, selectedCountry]);

  const contentPortalCountries = useMemo(
    () => cpCountriesRaw.map(countryFromApi),
    [cpCountriesRaw],
  );

  const contentPortalVerticals = useMemo(
    () => cpVerticalsRaw.map(verticalFromApi),
    [cpVerticalsRaw],
  );

  const contentPortalAttributes = useMemo(
    () => cpAttributesRaw.map(attributeFromApi),
    [cpAttributesRaw],
  );

  const contentPortalProducts = useMemo(
    () => cpProductsRaw.map(productFromApi),
    [cpProductsRaw],
  );

  const selectedContentPortalProduct = useMemo(
    () => contentPortalProducts.find((product) => product.id === selectedProductOffering),
    [contentPortalProducts, selectedProductOffering],
  );

  const countryOptions = useMemo(
    () => (isContentPortalProductsEnabledFlag
      ? contentPortalCountries.map((c) => ({ value: c.id, label: c.name }))
      : countriesConfig.map((c) => ({ value: c.code, label: c.name }))),
    [contentPortalCountries, countriesConfig, isContentPortalProductsEnabledFlag],
  );

  const verticalOptions = useMemo(() => {
    if (isContentPortalProductsEnabledFlag) {
      return contentPortalVerticals.map((v) => ({ value: v.id, label: v.name }));
    }
    const country = countriesConfig.find((c) => c.code === selectedCountry);
    if (country) {
      return country.verticals.map((v) => ({ value: v.code, label: v.name }));
    }
    return Object.entries(VERTICALS_DISPLAY_NAMES).map(([key, label]) => ({ value: key, label }));
  }, [contentPortalVerticals, countriesConfig, isContentPortalProductsEnabledFlag, selectedCountry]);

  const languageOptions = useMemo(() => {
    if (isContentPortalProductsEnabledFlag) {
      const country = contentPortalCountries.find((c) => c.id === selectedCountry);
      return country ? country.langs.map((l) => ({ value: l, label: langLabel(l) })) : [];
    }
    const country = countriesConfig.find((c) => c.code === selectedCountry);
    return country ? country.languages.map((l) => ({ value: l.code, label: l.name })) : [];
  }, [contentPortalCountries, countriesConfig, isContentPortalProductsEnabledFlag, selectedCountry]);

  const contentPortalProductOfferingOptions = useMemo(
    () => getContentPortalProductOptions(
      contentPortalProducts,
      selectedVertical,
      selectedLanguage || 'en',
    ),
    [contentPortalProducts, selectedLanguage, selectedVertical],
  );

  const contentPortalAttributeOptions = useMemo(
    () => getContentPortalAttributeOptions(
      selectedContentPortalProduct,
      contentPortalAttributes,
    ),
    [contentPortalAttributes, selectedContentPortalProduct],
  );

  useEffect(() => {
    if (!isContentPortalProductsEnabledFlag) return;

    setProductOfferingConfigMap((currentConfigMap) => {
      const nextConfigMap = { ...currentConfigMap };
      let hasNewConfig = false;

      contentPortalProducts.forEach((product) => {
        if (!product.id) return;
        const localizedConfig = buildContentPortalProductOfferingConfig(
          product,
          selectedLanguage || 'en',
        );
        const currentConfig = nextConfigMap[product.id];
        if (!currentConfig) {
          nextConfigMap[product.id] = localizedConfig;
          hasNewConfig = true;
          return;
        }

        if (!currentConfig.hasDisplayNameOverride && currentConfig.displayName !== localizedConfig.displayName) {
          nextConfigMap[product.id] = { ...currentConfig, displayName: localizedConfig.displayName };
          hasNewConfig = true;
        }
      });

      return hasNewConfig ? nextConfigMap : currentConfigMap;
    });
  }, [contentPortalProducts, isContentPortalProductsEnabledFlag, selectedLanguage, selectedVertical]);

  useEffect(() => {
    if (
      languageOptions.length > 0 &&
      !languageOptions.some((option) => option.value === selectedLanguage)
    ) {
      form.setFieldValue('language', languageOptions[0].value);
    }
  }, [form, languageOptions, selectedLanguage]);

  const effectiveProductOfferingOptions = isContentPortalProductsEnabledFlag
    ? contentPortalProductOfferingOptions
    : productOfferingOptions;
  const effectiveAttributeOptions = isContentPortalProductsEnabledFlag
    ? contentPortalAttributeOptions
    : attributeOptions;
  const isProductOfferingLoading = isContentPortalProductsEnabledFlag
    ? Boolean(cpCountriesRequest.isPending || cpVerticalsRequest.isPending || cpProductsRequest.isPending)
    : Boolean(legacyProductOfferingsRequest.isPending);
  const isAttributeLoading = isContentPortalProductsEnabledFlag
    ? Boolean(cpAttributesRequest.isPending)
    : Boolean(legacyAttributesRequest.isPending);

  useEffect(() => {
    if (!hasGenerated) return;

    if (legacyGenerateRequest.isPending) {
      setIsLoading(true);
      return;
    }

    if (legacyGenerateRequest.isSuccessful) {
      setContentItems(parseMystiqueContentItems(legacyContent));
      setError(null);
      setIsLoading(false);
      return;
    }

    if (legacyGenerateRequest.isFailure) {
      setContentItems([]);
      setError('Failed to generate content. Please try again.');
      setIsLoading(false);
    }
  }, [
    hasGenerated,
    legacyContent,
    legacyGenerateRequest.isFailure,
    legacyGenerateRequest.isPending,
    legacyGenerateRequest.isSuccessful,
  ]);

  const fetchProductOfferings = () => {
    const country = form.getFieldValue('country');
    const vertical = form.getFieldValue('vertical');

    if (!country || !vertical) return;

    if (isContentPortalProductsEnabledFlag) {
      dispatch(cpListProducts(country, STATUS_ACTIVE));
      dispatch(cpListCvps(country, STATUS_ACTIVE));
      return;
    }

    dispatch(contentGenerationListProductOfferings(country, vertical));
  };

  const fetchAttributePairs = () => {
    const country = form.getFieldValue('country');
    const vertical = form.getFieldValue('vertical');
    const productOffering = form.getFieldValue('productOffering');

    if (!country || !vertical || !productOffering) return;

    if (isContentPortalProductsEnabledFlag) {
      if (cpAttributesRaw.length === 0) {
        dispatch(cpListAttributes());
      }
      return;
    }

    dispatch(contentGenerationListAttributes(country, vertical, productOffering));
  };

  const openEditModal = (key: string) => {
    const selectedLang = form.getFieldValue('language') || 'en';
    const storedConfig = productOfferingConfigMap[key];
    const localizedConfig = isContentPortalProductsEnabledFlag && selectedContentPortalProduct
      ? buildContentPortalProductOfferingConfig(selectedContentPortalProduct, selectedLang)
      : null;
    const config = storedConfig || localizedConfig;
    const displayName = storedConfig && (!isContentPortalProductsEnabledFlag || storedConfig.hasDisplayNameOverride)
      ? storedConfig.displayName
      : (localizedConfig && localizedConfig.displayName) || (config && config.displayName) || '';
    const getEditValue = (map: Record<string, string> | undefined) => {
      if (!map) return '';
      return map[selectedLang]
        || (isContentPortalProductsEnabledFlag
          ? map.en || Object.values(map).find(Boolean) || ''
          : '');
    };
    setEditingConfig(config ? { ...config, displayName } : null);
    setEditingLanguage(selectedLang);
    editForm.setFieldsValue({
      displayName,
      productOverview: getEditValue(config && config.productOverview),
      reasonToUse: getEditValue(config && config.reasonToUse),
      callToAction: getEditValue(config && config.callToAction),
    });
    setIsEditModalOpen(true);
  };

  const handleProductOfferingChange = (value: string) => {
    form.resetFields(['attributes']);
    setAttributeOptions([]);
    if (!isContentPortalProductsEnabledFlag && value === CUSTOM_PO_KEY) {
      openEditModal(CUSTOM_PO_KEY);
    }
  };

  const handleCountryChange = () => {
    form.resetFields(['vertical', 'language', 'productOffering', 'attributes']);
    setProductOfferingOptions([]);
    setAttributeOptions([]);
    setProductOfferingConfigMap({});
  };

  const handleVerticalChange = () => {
    form.resetFields(['productOffering', 'attributes']);
    setProductOfferingOptions([]);
    setAttributeOptions([]);
    setProductOfferingConfigMap({});
  };

  const handleEditProductOffering = () => {
    const key = form.getFieldValue('productOffering');
    if (!key) return;
    openEditModal(key);
  };

  const handleSaveProductOffering = () => {
    editForm.validateFields().then((editValues) => {
      const config: ProductOfferingConfig = editingConfig || {
        key: CUSTOM_PO_KEY,
        displayName: '',
        productOverview: {},
        reasonToUse: {},
        callToAction: {},
        cvp: { exclusions: [], dependencies: [] },
        personalAttributes: null,
      };
      const defaultDisplayName = isContentPortalProductsEnabledFlag && selectedContentPortalProduct
        ? buildContentPortalProductOfferingConfig(selectedContentPortalProduct, editingLanguage).displayName
        : '';
      const hasDisplayNameOverride = isContentPortalProductsEnabledFlag
        ? String(editValues.displayName || '').trim() !== String(defaultDisplayName || '').trim()
        : config.hasDisplayNameOverride;
      const updatedConfig: ProductOfferingConfig = {
        key: config.key,
        displayName: editValues.displayName,
        hasDisplayNameOverride,
        productOverview: { ...config.productOverview, [editingLanguage]: editValues.productOverview },
        reasonToUse: { ...config.reasonToUse, [editingLanguage]: editValues.reasonToUse },
        callToAction: { ...config.callToAction, [editingLanguage]: editValues.callToAction },
        cvp: config.cvp,
        personalAttributes: config.personalAttributes,
      };
      setProductOfferingConfigMap({ ...productOfferingConfigMap, [updatedConfig.key]: updatedConfig });
      if (!isContentPortalProductsEnabledFlag && updatedConfig.key !== CUSTOM_PO_KEY) {
        setProductOfferingOptions(
          productOfferingOptions.map((o) =>
            (o.value === updatedConfig.key ? { value: o.value, label: updatedConfig.displayName } : o),
          ),
        );
      }
      setIsEditModalOpen(false);
    }).catch(() => {});
  };

  const handleGenerate = (values: FormValues) => {
    setIsLoading(true);
    setError(null);
    setHasGenerated(true);
    setContentItems([]);

    if (isContentPortalProductsEnabledFlag) {
      if (!hasContentPortalCvpsLoaded) {
        setError('Please wait for the country CVP catalog to load.');
        setIsLoading(false);
        return;
      }

      if (!selectedContentPortalProduct) {
        setError('Please select a valid product offering.');
        setIsLoading(false);
        return;
      }

      const payload = buildMystiqueV1OverridePayload(
        values,
        selectedContentPortalProduct,
        productOfferingConfigMap[values.productOffering],
        cpCvpsRaw,
      );
      setExcludedCvps(payload.excludedCVPs || []);
      dispatch(contentGenerationGenerateMystiqueV1(payload));
      return;
    }

    const verticalApi = values.vertical;
    const offeringConfig = productOfferingConfigMap[values.productOffering];

    const lang = values.language || 'en';
    const getLangValue = (map: Record<string, string> | undefined) =>
      (map && map[lang]) || '';

    const payload: Record<string, any> = {
      channel: 'push',
      countryCode: values.country,
      language: lang,
      vertical: verticalApi,
      productOffering: values.productOffering,
      segmentType:
        values.attributes && values.attributes.length > 0
          ? 'attributepairing'
          : null,
      customProductConfig: {
        productName: (offeringConfig && offeringConfig.displayName) || '',
        productOverview: getLangValue(offeringConfig && offeringConfig.productOverview),
        reasonToUse: getLangValue(offeringConfig && offeringConfig.reasonToUse),
        callToAction: getLangValue(offeringConfig && offeringConfig.callToAction),
        cvp: { exclusions: [] },
      },
    };

    const configExclusions = (offeringConfig && offeringConfig.cvp && offeringConfig.cvp.exclusions) || [];
    setExcludedCvps(configExclusions);

    if (values.attributes && values.attributes.length > 0) {
      payload.segmentName = values.attributes.join(', ');
    }
    if (values.dateRange && values.dateRange[0]) {
      payload.startDate = values.dateRange[0].format('YYYY-MM-DD');
    }
    if (values.dateRange && values.dateRange[1]) {
      payload.endDate = values.dateRange[1].format('YYYY-MM-DD');
    }

    dispatch(contentGenerationGenerateMystiqueV1(payload));
  };

  const handleDownloadCSV = () => {
    if (!contentItems.length) return;

    const values = form.getFieldsValue();
    const verticalLabel =
      VERTICALS_DISPLAY_NAMES[values.vertical] || values.vertical;
    const selectedProductLabel = isContentPortalProductsEnabledFlag && selectedContentPortalProduct
      ? productDisplayName(selectedContentPortalProduct, values.language || 'en')
      : values.productOffering || '';

    const rows = [
      ['country', 'vertical', 'product', 'attributes', 'start_date', 'end_date', 'cvp', 'score', 'title', 'message'],
      ...contentItems.map((item) => [
        values.country || '',
        verticalLabel,
        selectedProductLabel,
        (values.attributes || []).join(', '),
        (values.dateRange && values.dateRange[0] && values.dateRange[0].format('YYYY-MM-DD')) || '',
        (values.dateRange && values.dateRange[1] && values.dateRange[1].format('YYYY-MM-DD')) || '',
        item.cvp,
        item.score,
        item.title,
        item.message,
      ]),
    ];

    const csvContent = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
      )
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'marketing_content.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const disabledDate = (current: Dayjs) =>
    current && current < dayjs().startOf('day');

  const formValues = Form.useWatch([], form);

  const hasIncompletePoConfig = useMemo(() => {
    if (!formValues || !formValues.productOffering || !formValues.language) return false;
    const config = productOfferingConfigMap[formValues.productOffering];
    if (!config) return false;
    const lang = formValues.language;
    const hasConfigValue = (map: Record<string, string> | undefined) => {
      if (!map) return false;
      if (!isContentPortalProductsEnabledFlag) return Boolean(map[lang]);
      return Boolean(map[lang] || map.en || Object.values(map).find(Boolean));
    };
    return (
      !hasConfigValue(config.productOverview) ||
      !hasConfigValue(config.reasonToUse) ||
      !hasConfigValue(config.callToAction)
    );
  }, [formValues, isContentPortalProductsEnabledFlag, productOfferingConfigMap]);

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!formValues || !formValues.country) missing.push('Country');
    if (!formValues || !formValues.language) missing.push('Language');
    if (!formValues || !formValues.vertical) missing.push('Vertical');
    if (!formValues || !formValues.productOffering) missing.push('Product Offering');
    if (!isContentPortalProductsEnabledFlag && (!formValues || !formValues.attributes || formValues.attributes.length === 0)) {
      missing.push('Attributes');
    }
    if (isContentPortalProductsEnabledFlag && formValues && formValues.country && !hasContentPortalCvpsLoaded) {
      missing.push('Country CVP Catalog');
    }
    if (hasIncompletePoConfig) missing.push('Product Offering Config');
    return missing;
  }, [formValues, hasContentPortalCvpsLoaded, hasIncompletePoConfig, isContentPortalProductsEnabledFlag]);

  let productOfferingTooltipTitle;
  if (isContentPortalProductsEnabledFlag) {
    productOfferingTooltipTitle = 'Edit sandbox product config';
  } else if (hasIncompletePoConfig) {
    productOfferingTooltipTitle = 'Product offering config is incomplete for the selected language';
  }

  return (
    <Flex gap={24} style={{ width: '100%', alignItems: 'flex-start' }}>
      {/* Left: Form Panel */}
      <div
        style={{
          width: 320,
          flexShrink: 0,
          backgroundColor: colorBaseNeutralWhite,
          borderRadius: 12,
          padding: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Typography.Logo
          component="h5"
          fontType="solid"
          style={{ display: 'block', marginBottom: 20 }}
        >
          Generate Content
        </Typography.Logo>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerate}
          initialValues={{
            country: 'SG',
            vertical: 'transport',
            dateRange: [dayjs().add(1, 'day'), dayjs().add(8, 'day')],
          }}
        >
          <Form.Item
            label="Country"
            name="country"
            rules={[{ required: true, message: 'Please select a country' }]}
          >
            <Select placeholder="Select country" options={countryOptions} onChange={handleCountryChange} />
          </Form.Item>

          <Form.Item
            label="Language"
            name="language"
            rules={[{ required: true, message: 'Please select a language' }]}
          >
            <Select placeholder="Select language" options={languageOptions} />
          </Form.Item>

          <Form.Item
            label="Vertical"
            name="vertical"
            rules={[{ required: true, message: 'Please select a vertical' }]}
          >
            <Select placeholder="Select vertical" options={verticalOptions} onChange={handleVerticalChange} />
          </Form.Item>

          <Form.Item label="Product Offering" required>
            <Flex gap={8} align="center">
              <Form.Item
                name="productOffering"
                rules={[
                  { required: true, message: 'Please select a product offering' },
                ]}
                noStyle
              >
                <Select
                  style={{ flex: 1, minWidth: 0 }}
                  placeholder="Select product offering"
                  options={effectiveProductOfferingOptions}
                  loading={isProductOfferingLoading}
                  onFocus={fetchProductOfferings}
                  onChange={handleProductOfferingChange}
                  notFoundContent={isProductOfferingLoading ? 'Loading...' : undefined}
                />
              </Form.Item>
              <Tooltip title={productOfferingTooltipTitle}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEditProductOffering}
                  disabled={!form.getFieldValue('productOffering')}
                  danger={hasIncompletePoConfig}
                />
              </Tooltip>
            </Flex>
          </Form.Item>

          <Form.Item
            label="Attributes"
            name="attributes"
            dependencies={['productOffering']}
            tooltip={`Select up to ${MAX_ATTRIBUTES} attribute pairings`}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    if (isContentPortalProductsEnabledFlag) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please select at least one attribute'));
                  }
                  if (value.length > MAX_ATTRIBUTES) {
                    return Promise.reject(new Error(`Maximum ${MAX_ATTRIBUTES} attributes allowed`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Search and select attributes"
              options={effectiveAttributeOptions}
              loading={isAttributeLoading}
              onFocus={fetchAttributePairs}
              notFoundContent={isAttributeLoading ? 'Loading...' : undefined}
              tagRender={(props) => {
                const attr = effectiveAttributeOptions.find((a) => a.value === props.value);
                const isWhitelisted = attr ? attr.isWhitelisted : true;
                return (
                  <Tag
                    closable={props.closable}
                    onClose={props.onClose}
                    style={{
                      backgroundColor: isWhitelisted ? colorStatusNeutralSoftest : colorStatusAlertSoftest,
                      color: isWhitelisted ? colorStatusNeutralBold : colorStatusAlertBold,
                      border: 'none',
                      borderRadius: 4,
                      margin: '2px 4px 2px 0',
                    }}
                  >
                    {props.label}
                  </Tag>
                );
              }}
              optionRender={(option) => {
                const optAttr = effectiveAttributeOptions.find((a) => a.value === option.value);
                const isWhitelisted = optAttr ? optAttr.isWhitelisted : true;
                const tooltipText =
                  `${isWhitelisted ? 'Whitelisted' : 'Non-whitelisted'} attribute - ${(optAttr && optAttr.definition) || option.label}`;
                return (
                  <Tooltip title={tooltipText} placement="right">
                    <Flex align="center" gap={8} style={{ width: '100%' }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: isWhitelisted ? colorStatusNeutralBold : colorStatusAlertBold,
                        }}
                      />
                      {option.label}
                    </Flex>
                  </Tooltip>
                );
              }}
            />
          </Form.Item>

          <Form.Item label="Date Range" name="dateRange">
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              disabledDate={disabledDate}
              format="MM/DD/YYYY"
              placeholder={['Start date', 'End date']}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Tooltip
              title={
                missingFields.length > 0 ? (
                  <div>
                    Please fill in:
                    <ul style={{ margin: '4px 0 0 0', paddingLeft: 16 }}>
                      {missingFields.map((f) => <li key={f}>{f}</li>)}
                    </ul>
                  </div>
                ) : undefined
              }
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={missingFields.length > 0}
                style={{ width: '100%' }}
                icon={<AiOutlined />}
              >
                Generate Content
              </Button>
            </Tooltip>
          </Form.Item>
        </Form>
      </div>

      {/* Right: Results Panel */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Empty initial state */}
        {!hasGenerated && (
          <Flex
            vertical
            align="center"
            justify="center"
            gap={12}
            style={{
              height: 400,
              backgroundColor: colorBackgroundAlt,
              borderRadius: 12,
            }}
          >
            <AiOutlined
              style={{ fontSize: 48, color: colorContentDim }}
            />
            <Typography.Body type="secondary">
              Select your preferences and click &apos;Generate Content&apos;
            </Typography.Body>
          </Flex>
        )}

        {/* Loading state */}
        {hasGenerated && isLoading && (
          <Flex align="center" justify="center" style={{ height: 400 }}>
            <Spin size="large" />
          </Flex>
        )}

        {/* Error state */}
        {hasGenerated && !isLoading && error && (
          <Flex
            vertical
            align="center"
            justify="center"
            style={{
              height: 400,
              backgroundColor: colorBackgroundAlt,
              borderRadius: 12,
              padding: 24,
            }}
          >
            <Typography.Body type="danger">{error}</Typography.Body>
          </Flex>
        )}

        {/* No results */}
        {hasGenerated && !isLoading && !error && contentItems.length === 0 && (
          <Flex
            vertical
            align="center"
            justify="center"
            style={{
              height: 400,
              backgroundColor: colorBackgroundAlt,
              borderRadius: 12,
            }}
          >
            <Typography.Body type="secondary">
              No content generated. Try different parameters.
            </Typography.Body>
          </Flex>
        )}

        {/* Results grid */}
        {!isLoading && contentItems.length > 0 && (
          <React.Fragment>
            <Flex
              justify="space-between"
              align="center"
              style={{ marginBottom: 16 }}
            >
              <Flex align="center" gap={8}>
                <Typography.Logo component="h5" fontType="solid">
                  Generated Content
                </Typography.Logo>
                <Tag
                  style={{
                    background: colorBrandPrimarySoftest,
                    color: colorBrandPrimaryBold,
                    border: 'none',
                    borderRadius: 12,
                  }}
                >
                  {contentItems.length}
                </Tag>
              </Flex>
              <Button
                icon={<DownloadUniversal />}
                onClick={handleDownloadCSV}
                size="small"
              >
                Download CSV
              </Button>
            </Flex>

            <Row gutter={[16, 16]}>
              {contentItems.map((item, idx) => {
                const isFail = item.reviewResult === 'fail';
                const itemCvpLower = item.cvp ? item.cvp.toLowerCase() : '';
                const isExcluded = itemCvpLower !== '' && excludedCvps.some((excl) => excl.toLowerCase() === itemCvpLower);
                return (
                  <Col key={idx} xs={24} sm={12}>
                    <Card
                      bordered={false}
                      style={{
                        height: '100%',
                        borderRadius: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                      styles={{
                        body: {
                          padding: 20,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        },
                      }}
                    >
                      <Typography.Body
                        strong
                        style={{ display: 'block', marginBottom: 8 }}
                      >
                        {item.title}
                      </Typography.Body>

                      <Typography.Body
                        type="secondary"
                        style={{ display: 'block', flex: 1, marginBottom: 16 }}
                      >
                        {item.message}
                      </Typography.Body>

                      <Flex justify="space-between" align="center">
                        {item.cvp ? (
                          <Tooltip title={`${isExcluded ? 'Excluded' : 'Included'} CVP - ${item.cvp}`}>
                            <Tag
                              style={{
                                background: isExcluded ? colorStatusAlertSoftest : colorBackgroundAlt,
                                border: isExcluded ? `1px solid ${colorStatusAlertBold}` : 'none',
                                color: isExcluded ? colorStatusAlertBold : undefined,
                                borderRadius: 12,
                              }}
                            >
                              CVP: {item.cvp}
                            </Tag>
                          </Tooltip>
                        ) : (
                          <span />
                        )}
                        <Tooltip title={item.reviewReason}>
                          <Tag
                            style={{
                              background: isFail ? colorStatusAlertSoftest : colorStatusPositiveSoftest,
                              color: isFail ? colorStatusAlertBold : colorStatusPositiveBold,
                              border: 'none',
                              borderRadius: 12,
                              fontWeight: 600,
                            }}
                          >
                            Score: {item.score}
                          </Tag>
                        </Tooltip>
                      </Flex>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </React.Fragment>
        )}
      </div>

      <Modal
        title={editingConfig ? 'Edit Product Offering Config' : 'Configure Custom Product Offering'}
        open={isEditModalOpen}
        onOk={handleSaveProductOffering}
        onCancel={() => { editForm.resetFields(); setIsEditModalOpen(false); }}
        okText="Save"
        width={600}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Display Name"
            name="displayName"
            rules={[{ required: true, message: 'Please enter a display name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={`Product Overview (${editingLanguage.toUpperCase()})`}
            name="productOverview"
            rules={[{ required: true, message: 'Please enter a product overview' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label={`Reason to Use (${editingLanguage.toUpperCase()})`}
            name="reasonToUse"
            rules={[{ required: true, message: 'Please enter a reason to use' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label={`Call to Action (${editingLanguage.toUpperCase()})`}
            name="callToAction"
            rules={[{ required: true, message: 'Please enter a call to action' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

export default ContentGenerationView;
